import { createUseStyles } from "react-jss";
import type { Theme } from "../../types/Theme";
import { alpha } from "../../theme/Mixins";
import { resolveFieldBorderWidth } from "../../theme/inputThemeTokens";
import {
    MODERN_SIGNATURE_BASELINE_MARGIN_TOP_PX,
    MODERN_SIGNATURE_CLEAR_BUTTON_SIZE_PX,
    MODERN_SIGNATURE_CLEAR_ICON_SIZE_PX,
    MODERN_SIGNATURE_CLEAR_INSET_PX,
    MODERN_SIGNATURE_HINT_GAP_PX,
    MODERN_SIGNATURE_HINT_PADDING_PX,
    resolveModernSignatureBaselineColor,
    resolveModernSignatureBorderColor,
    resolveModernSignatureClearFocusBoxShadow,
    resolveModernSignatureClearHoverBackground,
    resolveModernSignatureClearIconColor,
    resolveModernSignatureHintColor,
    resolveModernSignatureHintFontSize,
    useModernSignatureStyles,
} from "../../theme/signatureThemeTokens";

const cornerSize = 5;
const cornerWidth = 2;
const borderRadiuses = {
    squared: 0,
    soft: 15,
    rounded: 50,
};

const makeCornerStyle = () => ({
    position: "absolute",
    pointerEvents: "none",
    width: `${cornerSize}%`,
    margin: ({ theme }: { theme: Theme }) =>
        Math.min(theme.BorderRadius, borderRadiuses.rounded) >= borderRadiuses.rounded ? 20 : 12,
    height: ({ ratio }: { ratio: number }) => `${cornerSize * ratio}%`,
});

export default createUseStyles({
    instruction: {
        marginTop: 0,
        marginBottom: 8,
    },
    srOnly: {
        position: "absolute",
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: "hidden",
        clip: "rect(0,0,0,0)",
        whiteSpace: "nowrap",
        border: 0,
    },
    container: {
        display: "inline-block",
        background: ({ theme }: { theme: Theme }) => alpha(theme.InputBackground, theme.ControlAlpha),
        borderRadius: ({ theme }: { theme: Theme }) => Math.min(theme.BorderRadius, 50),
        border: ({ theme }: { theme: Theme }) =>
            useModernSignatureStyles(theme)
                ? `${resolveFieldBorderWidth(theme)}px solid ${resolveModernSignatureBorderColor()}`
                : [[theme.BorderWidth, "solid", theme.ControlBorderColor]],
    },
    signatureArea: {
        position: "relative",
    },
    canvas: {
        cursor: "crosshair",
        display: "block",
        width: ({ signatureWidth }: { signatureWidth: number }) => `${signatureWidth}px`,
        height: ({ signatureWidth, ratio }: { signatureWidth: number; ratio: number }) =>
            `${signatureWidth / ratio}px`,
        touchAction: ({ theme }: { theme: Theme }) =>
            useModernSignatureStyles(theme) ? "none" : undefined,
        background: ({ theme }: { theme: Theme }) =>
            useModernSignatureStyles(theme) ? "transparent" : undefined,
    },
    image: {
        width: ({ signatureWidth }: { signatureWidth: number }) => `${signatureWidth}px`,
        height: ({ signatureWidth, ratio }: { signatureWidth: number; ratio: number }) =>
            `${signatureWidth / ratio}px`,
        cursor: "not-allowed",
        display: "block",
    },
    undoButton: {
        position: "absolute",
        top: "20%",
        left: "10%",
        marginBottom: ({ signatureWidth }: { signatureWidth: number }) =>
            `${signatureWidth * 0.03}px`,
        cursor: "pointer",
    },
    clearButton: {
        position: "absolute",
        top: "20%",
        right: "10%",
        marginBottom: ({ signatureWidth }: { signatureWidth: number }) =>
            `${signatureWidth * 0.03}px`,
        cursor: "pointer",
    },
    clearButtonModern: {
        composes: "signature__clear--modern",
        position: "absolute",
        top: MODERN_SIGNATURE_CLEAR_INSET_PX,
        right: MODERN_SIGNATURE_CLEAR_INSET_PX,
        zIndex: 10,
        alignItems: "center",
        background: "transparent",
        border: "none",
        borderRadius: "50%",
        boxShadow: "none",
        boxSizing: "border-box",
        color: ({ theme }: { theme: Theme }) => resolveModernSignatureClearIconColor(theme),
        cursor: "pointer",
        display: "flex",
        fontFamily: ({ theme }: { theme: Theme }) => `"${theme.TextFont}", sans-serif`,
        height: MODERN_SIGNATURE_CLEAR_BUTTON_SIZE_PX,
        justifyContent: "center",
        minHeight: MODERN_SIGNATURE_CLEAR_BUTTON_SIZE_PX,
        minWidth: MODERN_SIGNATURE_CLEAR_BUTTON_SIZE_PX,
        outline: "none",
        padding: 4,
        width: MODERN_SIGNATURE_CLEAR_BUTTON_SIZE_PX,
        transition: "background-color 200ms ease",
        "&:hover": {
            background: resolveModernSignatureClearHoverBackground(),
            boxShadow: "none",
        },
        "&:focus-visible": {
            boxShadow: ({ theme }: { theme: Theme }) =>
                resolveModernSignatureClearFocusBoxShadow(theme),
            outline: "none",
        },
        "& .button__container": {
            height: MODERN_SIGNATURE_CLEAR_ICON_SIZE_PX,
            lineHeight: `${MODERN_SIGNATURE_CLEAR_ICON_SIZE_PX}px`,
            margin: 0,
        },
        "& .button__icon": {
            left: 0,
            position: "initial",
            right: 0,
        },
    },
    undoButtonModern: {
        composes: "signature__undo--modern",
        position: "absolute",
        top: MODERN_SIGNATURE_CLEAR_INSET_PX,
        left: MODERN_SIGNATURE_CLEAR_INSET_PX,
        zIndex: 10,
        alignItems: "center",
        background: "transparent",
        border: "none",
        borderRadius: "50%",
        boxShadow: "none",
        boxSizing: "border-box",
        color: ({ theme }: { theme: Theme }) => resolveModernSignatureClearIconColor(theme),
        cursor: "pointer",
        display: "flex",
        fontFamily: ({ theme }: { theme: Theme }) => `"${theme.TextFont}", sans-serif`,
        height: MODERN_SIGNATURE_CLEAR_BUTTON_SIZE_PX,
        justifyContent: "center",
        minHeight: MODERN_SIGNATURE_CLEAR_BUTTON_SIZE_PX,
        minWidth: MODERN_SIGNATURE_CLEAR_BUTTON_SIZE_PX,
        outline: "none",
        padding: 4,
        width: MODERN_SIGNATURE_CLEAR_BUTTON_SIZE_PX,
        transition: "background-color 200ms ease",
        "&:hover": {
            background: resolveModernSignatureClearHoverBackground(),
            boxShadow: "none",
        },
        "&:focus-visible": {
            boxShadow: ({ theme }: { theme: Theme }) =>
                resolveModernSignatureClearFocusBoxShadow(theme),
            outline: "none",
        },
        "& .button__container": {
            height: MODERN_SIGNATURE_CLEAR_ICON_SIZE_PX,
            lineHeight: `${MODERN_SIGNATURE_CLEAR_ICON_SIZE_PX}px`,
            margin: 0,
        },
        "& .button__icon": {
            left: 0,
            position: "initial",
            right: 0,
        },
    },
    signatureLine: {
        position: "absolute",
        bottom: "20%",
        left: "10%",
        right: "10%",
        pointerEvents: "none",
        fontSize: ({ signatureWidth }: { signatureWidth: number }) => signatureWidth * 0.04,
        lineHeight: ({ signatureWidth }: { signatureWidth: number }) =>
            `${signatureWidth * 0.1}px`,
        borderBottom: "2px solid #999",
    },
    hintOverlay: {
        composes: "signature__hint-overlay",
        position: "absolute",
        inset: `auto 0 0 0`,
        display: "flex",
        flexDirection: "column",
        padding: `0 ${MODERN_SIGNATURE_HINT_PADDING_PX}px ${MODERN_SIGNATURE_HINT_PADDING_PX}px`,
        pointerEvents: "none",
    },
    hintRow: {
        composes: "signature__hint-row",
        alignItems: "center",
        color: resolveModernSignatureHintColor(),
        display: "flex",
        fontFamily: ({ theme }: { theme: Theme }) => `"${theme.TextFont}", sans-serif`,
        fontSize: ({ theme }: { theme: Theme }) => resolveModernSignatureHintFontSize(theme),
        fontWeight: 400,
        gap: MODERN_SIGNATURE_HINT_GAP_PX,
    },
    hintHidden: {
        visibility: "hidden",
    },
    baselineModern: {
        composes: "signature__baseline",
        backgroundColor: resolveModernSignatureBaselineColor(),
        height: 1,
        marginTop: MODERN_SIGNATURE_BASELINE_MARGIN_TOP_PX,
        width: "100%",
    },
    topLeftCorner: {
        ...makeCornerStyle(),
        top: 0,
        left: 0,
        borderTop: ({ theme }: { theme: Theme }) =>
            `${cornerWidth}px solid ${theme.ControlBorderColor}`,
        borderLeft: ({ theme }: { theme: Theme }) =>
            `${cornerWidth}px solid ${theme.ControlBorderColor}`,
    },
    topRightCorner: {
        ...makeCornerStyle(),
        top: 0,
        right: 0,
        borderTop: ({ theme }: { theme: Theme }) =>
            `${cornerWidth}px solid ${theme.ControlBorderColor}`,
        borderRight: ({ theme }: { theme: Theme }) =>
            `${cornerWidth}px solid ${theme.ControlBorderColor}`,
    },
    bottomLeftCorner: {
        ...makeCornerStyle(),
        bottom: 0,
        left: 0,
        borderBottom: ({ theme }: { theme: Theme }) =>
            `${cornerWidth}px solid ${theme.ControlBorderColor}`,
        borderLeft: ({ theme }: { theme: Theme }) =>
            `${cornerWidth}px solid ${theme.ControlBorderColor}`,
    },
    bottomRightCorner: {
        ...makeCornerStyle(),
        bottom: 0,
        right: 0,
        borderBottom: ({ theme }: { theme: Theme }) =>
            `${cornerWidth}px solid ${theme.ControlBorderColor}`,
        borderRight: ({ theme }: { theme: Theme }) =>
            `${cornerWidth}px solid ${theme.ControlBorderColor}`,
    },
});
